/* eslint-disable complexity, max-lines */
import {
  apply_for_onboarding_entity_type,
  execute_created_onboarding_case,
  fetch_all_assigned_onboarding_cases,
  fetch_application_form,
  fetch_application_forms,
  fetch_onboarding_entity_types,
  fetch_onboarding_execution_status,
  persist_local_onboarding_case_metadata,
  submit_application_form_answers,
  submit_application_form_file_answers
} from '@/services/onboarding_journey_service';
import {
  first_value,
  normalize_main_section,
  normalize_text,
  extract_saved_answers
} from '@/services/onboarding_form_helpers';
import {
  prefill_question_value
} from './intake_portal_helpers';
import { intake_portal_methods } from './intake_portal_methods';
import FacialVerificationField from './FacialVerificationField.vue';
import {
  build_onboarding_creation_options,
  build_onboarding_metadata_payload,
  find_case_for_entity_type,
  get_entity_type_option_id,
  get_entity_type_option_name
} from './intake_portal_journey_helpers';

const DEFAULT_PAGE_SUBTITLE = 'View your onboarding progress and complete the forms assigned to you.';
const DEFAULT_SECTION_SUBTITLE = 'Complete the required details for this section before moving to the next step.';

function compact_text(value) {
  return String(value || '').trim();
}

function unique_values(values = []) {
  const unique_items = Array.from(new Set(values.map(compact_text).filter(Boolean)));
  return unique_items;
}

function get_status_request_ids(case_item = {}, application_id = 0) {
  const entity_type_id = Number(case_item.entity_type_id || case_item.threshold_entity_type_id_used || 0);
  const entity_profile_id = Number(case_item.entity_profile_id || 0);
  return { entity_type_id, entity_profile_id, application_id };
}

function can_load_status(request_ids = {}) {
  return Boolean(request_ids.entity_type_id && request_ids.entity_profile_id && request_ids.application_id);
}

function get_form_main_sections(detail = {}) {
  const main_sections = first_value([
    detail?.main_sections,
    detail?.form?.main_sections,
    detail?.form?.sections,
    detail?.sections
  ], []);
  return Array.isArray(main_sections) ? main_sections : [];
}

function build_question_section(detail = {}, form_name = 'Assigned form') {
  const questions = detail?.form?.questions;
  if (!Array.isArray(questions)) return [];
  return [{ name: form_name, questions }];
}

function get_first_application_id(cases = []) {
  const application_id = cases[0]?.application_id;
  return application_id ? String(application_id) : '';
}

function get_error_message(error, fallback = '') {
  return error?.response?.data?.message || error?.message || fallback;
}

function should_hide_matching_section_title(sub_section_label, main_section_label, total_sub_sections) {
  return total_sub_sections === 1 && sub_section_label === main_section_label;
}

function should_render_sub_section_title(sub_section_label, main_section_label, total_sub_sections) {
  if (!sub_section_label) return false;
  if (sub_section_label === 'details') return false;
  return !should_hide_matching_section_title(sub_section_label, main_section_label, total_sub_sections);
}

function show_system_loader(message) {
  window.dispatchEvent(new CustomEvent('system-loader:show', {
    detail: message
  }));
}

function hide_system_loader_after_render(instance) {
  instance.$nextTick(() => {
    window.dispatchEvent(new CustomEvent('system-loader:hide'));
  });
}

export default {
  name: 'OnboardingIntakePortal',
  components: {
    FacialVerificationField
  },
  data: () => ({
    is_loading_portal: false,
    portal_error: '',
    is_submitting: false,
    is_starting_onboarding: false,
    submit_success_message: '',
    applicant_cases: [],
    entity_type_options: [],
    selected_entity_type_id: '',
    application_forms: [],
    onboarding_status: null,
    intake_steps: [],
      current_step: 1,
      selected_application_id: '',
      selected_application_form_id: '',
      selected_form_id: '',
      selected_form_detail: null,
      form_values: {},
      file_values: {},
      persisted_file_urls: {},
      facial_verification_values: {}
  }),
  computed: {
    selected_case() {
      return this.applicant_cases.find((item) => String(item.application_id) === String(this.selected_application_id)) || null;
    },
    selected_existing_case() {
      return find_case_for_entity_type(this.applicant_cases, this.selected_entity_type_id);
    },
    selected_entity_type() {
      return this.entity_type_options.find((item) => (
        String(get_entity_type_option_id(item)) === String(this.selected_entity_type_id)
      )) || null;
    },
    selected_application_form() {
      return this.application_forms.find((form) => String(this.get_application_form_id(form)) === String(this.selected_application_form_id)) || null;
    },
    current_step_index() {
      return Math.max(0, this.current_step - 1);
    },
    current_main_section() {
      return this.intake_steps[this.current_step_index] || null;
    },
    current_step_label() {
      return `Step ${this.current_step} of ${this.intake_steps.length || 1}`;
    },
    page_title() {
      return this.selected_journey_label || this.selected_case?.entity_name || this.selected_case?.title || 'Choose your onboarding';
    },
    page_subtitle() {
      return DEFAULT_PAGE_SUBTITLE;
    },
    card_title() {
      return this.current_main_section?.label || this.selected_application_form_name || 'Assigned form';
    },
    card_subtitle() {
      return this.current_main_section?.description || DEFAULT_SECTION_SUBTITLE;
    },
      selected_application_form_name() {
        return this.get_application_form_name(this.selected_application_form);
      },
      current_form_instructions() {
        const instructions = Array.isArray(this.selected_form_detail?.form?.form_instructions)
          ? this.selected_form_detail.form.form_instructions
          : Array.isArray(this.selected_form_detail?.form_instructions)
            ? this.selected_form_detail.form_instructions
            : [];

        return instructions
          .map((instruction) => String(first_value([
            instruction?.content,
            instruction?.text,
            instruction?.description
          ], '')).trim())
          .filter(Boolean);
      },
      is_last_step() {
        return this.current_step >= this.intake_steps.length;
      },
    submit_button_label() {
      return this.is_last_step ? 'Submit form' : 'Continue';
    },
    completion_label() {
      const completion = this.onboarding_status?.completion || {};
      return completion.overall_complete ? 'Complete' : 'In progress';
    },
    selected_journey_label() {
      return this.selected_entity_type ? get_entity_type_option_name(this.selected_entity_type) : '';
    },
    intake_action_label() {
      if (this.is_starting_onboarding) return 'Preparing...';
      return this.selected_existing_case?.application_id ? 'Continue intake' : 'Start intake';
    },
    can_start_selected_intake() {
      return Boolean(this.selected_entity_type_id) && !this.is_starting_onboarding;
    },
    empty_form_title() {
      return this.selected_application_id ? 'No form available' : 'Select an onboarding type';
    },
    empty_form_subtitle() {
      return this.selected_application_id
        ? 'This onboarding type has no assigned forms available yet.'
        : 'Choose the journey you want to complete, then the assigned form will open here.';
    },
    has_entity_type_options() {
      return this.entity_type_options.length > 0;
    },
    show_case_switcher() {
      return this.application_forms.length > 1;
    }
  },
  async mounted() {
    await this.load_portal();
  },
  methods: {
    get_application_form_id(form) {
      return Number(first_value([form?.application_form_id, form?.id], 0)) || null;
    },
    get_application_form_definition_id(form) {
      return Number(first_value([form?.form?.id, form?.form_id, form?.form_company_id], 0)) || null;
    },
    get_application_form_name(form) {
      return first_value([form?.form?.name, form?.form?.form_name, form?.form_name, form?.name], 'Assigned form');
    },
    get_entity_type_option_id(entity_type) {
      return get_entity_type_option_id(entity_type);
    },
    entity_type_button_class(entity_type) {
      const entity_type_id = get_entity_type_option_id(entity_type);
      const is_active = String(entity_type_id) === String(this.selected_entity_type_id);
      const classes = ['intake-portal-journey-pill', { 'is-active': is_active }];
      return classes;
    },
    entity_type_label(entity_type) {
      return get_entity_type_option_name(entity_type);
    },
    get_case_option_label(case_item = {}) {
      const context_parts = unique_values([
        case_item.journey,
        case_item.onboarding_project_name,
        case_item.owner,
        case_item.activation
      ]);
      const fallback_name = first_value([case_item.entity_name, case_item.title, case_item.display_name], 'Onboarding');
      const application_id = compact_text(case_item.application_id);
      const label_base = context_parts.length ? context_parts.join(' - ') : fallback_name;
      const label = application_id ? `${label_base} - #${application_id}` : label_base;
      return label;
    },
    reset_form_state() {
      this.portal_error = '';
      this.submit_success_message = '';
      this.intake_steps = [];
      this.current_step = 1;
      this.selected_form_detail = null;
      this.form_values = {};
      this.file_values = {};
      this.persisted_file_urls = {};
      this.facial_verification_values = {};
    },
    async load_portal() {
      this.is_loading_portal = true;
      this.portal_error = '';
      show_system_loader('Loading intake...');
      try {
        const [cases, entity_types] = await Promise.all([
          fetch_all_assigned_onboarding_cases({ _: Date.now() }),
          fetch_onboarding_entity_types()
        ]);
        this.applicant_cases = cases;
        this.entity_type_options = entity_types;
        this.selected_entity_type_id = this.get_initial_entity_type_id(cases, entity_types);
        await this.load_or_start_selected_entity_type(false);
      } catch (error) {
        this.portal_error = get_error_message(error, 'Unable to load your onboarding.');
      } finally {
        this.is_loading_portal = false;
        hide_system_loader_after_render(this);
      }
    },
    get_initial_entity_type_id(cases = [], entity_types = []) {
      const first_case_entity_type_id = Number(cases[0]?.entity_type_id || cases[0]?.threshold_entity_type_id_used || 0);
      if (first_case_entity_type_id > 0) return String(first_case_entity_type_id);

      const first_entity_type_id = get_entity_type_option_id(entity_types[0]);
      return first_entity_type_id > 0 ? String(first_entity_type_id) : '';
    },
    async handleEntityTypeChange(entity_type) {
      const entity_type_id = get_entity_type_option_id(entity_type);
      if (!entity_type_id || String(entity_type_id) === String(this.selected_entity_type_id)) return;

      this.selected_entity_type_id = String(entity_type_id);
      await this.load_or_start_selected_entity_type(false);
    },
    async handleStartSelectedIntake() {
      await this.load_or_start_selected_entity_type(true);
    },
    async load_or_start_selected_entity_type(allow_create) {
      this.reset_selected_application();
      if (!this.selected_entity_type_id) return;

      const existing_case = find_case_for_entity_type(this.applicant_cases, this.selected_entity_type_id);
      if (existing_case?.application_id) {
        this.selected_application_id = String(existing_case.application_id);
        await this.load_selected_application();
        return;
      }

      if (allow_create) await this.start_selected_entity_type_onboarding();
    },
    reset_selected_application() {
      this.selected_application_id = '';
      this.selected_application_form_id = '';
      this.selected_form_id = '';
      this.selected_form_detail = null;
      this.application_forms = [];
      this.onboarding_status = null;
      this.reset_form_state();
    },
    async start_selected_entity_type_onboarding() {
      this.is_starting_onboarding = true;
      this.portal_error = '';
      show_system_loader('Preparing onboarding...');
      try {
        const creation_response = await apply_for_onboarding_entity_type(
          build_onboarding_creation_options(this.selected_entity_type_id)
        );
        persist_local_onboarding_case_metadata(build_onboarding_metadata_payload({
          creation_response,
          selected_entity_type: this.selected_entity_type
        }));
        await execute_created_onboarding_case({
          creation_response,
          entity_type_id: Number(this.selected_entity_type_id)
        });
        await this.refresh_cases_and_open_selected_type();
      } catch (error) {
        this.portal_error = get_error_message(error, 'Unable to start this onboarding.');
      } finally {
        this.is_starting_onboarding = false;
        hide_system_loader_after_render(this);
      }
    },
    async refresh_cases_and_open_selected_type() {
      this.applicant_cases = await fetch_all_assigned_onboarding_cases({ _: Date.now() });
      const next_case = find_case_for_entity_type(this.applicant_cases, this.selected_entity_type_id);
      this.selected_application_id = next_case?.application_id ? String(next_case.application_id) : get_first_application_id(this.applicant_cases);
      await this.load_selected_application();
    },
    async load_selected_application() {
      this.reset_form_state();
      this.application_forms = [];
      this.onboarding_status = null;
      if (!this.selected_application_id) return;

      const application_id = Number(this.selected_application_id);
      const [forms, status] = await Promise.all([
        fetch_application_forms(application_id),
        this.load_onboarding_status(application_id)
      ]);
      this.application_forms = forms;
      this.onboarding_status = status;
      this.selected_application_form_id = this.get_application_form_id(forms[0]) || '';
      await this.load_selected_form_structure();
    },
    async load_onboarding_status(application_id) {
      const case_item = this.selected_case || {};
      const request_ids = get_status_request_ids(case_item, application_id);
      if (!can_load_status(request_ids)) return null;

      return fetch_onboarding_execution_status(request_ids).catch(() => null);
    },
    async load_selected_form_structure() {
      this.reset_form_state();
      const application_form_id = Number(this.selected_application_form_id || 0);
      if (!application_form_id) return;

      try {
        const detail = await fetch_application_form(application_form_id);
        const structure = this.resolve_form_structure(detail);
        this.selected_form_detail = detail;
        this.selected_form_id = this.get_application_form_definition_id(detail);
        this.intake_steps = structure.map((item, index) => normalize_main_section(item, index));
        this.prefill_default_answers(detail);
      } catch (error) {
        this.portal_error = get_error_message(error, 'Unable to load the assigned form.');
      }
    },
    resolve_form_structure(detail) {
      const main_sections = get_form_main_sections(detail);
      if (main_sections.length) return main_sections;
      return build_question_section(detail, this.get_application_form_name(detail));
    },
    prefill_default_answers(detail) {
      const saved_answers = extract_saved_answers(detail);
      const saved_map = new Map(saved_answers.map((answer) => [String(answer.section_question_id), answer]));
      this.intake_steps.forEach((main_section) => {
        main_section.sub_sections.forEach((sub_section) => {
          sub_section.questions.forEach((question) => {
            const saved_answer = saved_map.get(String(question.id));
            this.prefill_question(question, saved_answer);
          });
        });
      });
    },
    prefill_question(question, saved_answer) {
      prefill_question_value({
        question: {
          ...question,
          answer: question.type === 'file'
            ? saved_answer || question.answer
            : saved_answer?.answer_text || question.answer
        },
        form_values: this.form_values,
        file_values: this.file_values,
        persisted_file_urls: this.persisted_file_urls
      });
    },
    is_active_step(step) {
      return step.number === this.current_step;
    },
    go_to_next_step() {
      if (this.current_step < this.intake_steps.length) this.current_step += 1;
    },
    go_to_previous_step() {
      if (this.current_step > 1) this.current_step -= 1;
    },
    async submit_or_continue() {
      if (!this.validate_current_step()) return;
      if (!this.is_last_step) {
        this.go_to_next_step();
        return;
      }
      await this.submit_intake_form();
    },
    ...intake_portal_methods,
    should_show_sub_section_title(sub_section) {
      const sub_section_label = normalize_text(sub_section?.label);
      const main_section_label = normalize_text(this.current_main_section?.label);
      const total_sub_sections = this.current_main_section?.sub_sections?.length || 0;
      return should_render_sub_section_title(sub_section_label, main_section_label, total_sub_sections);
    },
    async submit_standard_answers(application_form_id) {
      const standard_answers = this.buildStandardAnswers();
      if (!standard_answers.length) return;
      await submit_application_form_answers({
        application_id: Number(this.selected_application_id),
        application_form_id,
        answers: standard_answers
      });
    },
    async submit_file_answers(application_form_id) {
      const file_payload = this.buildFileAnswerPayload();
      if (!file_payload.answers.length) return;
      await submit_application_form_file_answers({
        application_id: Number(this.selected_application_id),
        application_form_id,
        answers: file_payload.answers,
        files: file_payload.files
      });
    },
    async submit_intake_form() {
      const application_form_id = Number(this.selected_application_form_id || 0);
      if (!application_form_id) {
        this.portal_error = 'Select an assigned form before submitting.';
        return;
      }
      this.is_submitting = true;
      this.portal_error = '';
      this.submit_success_message = '';
      try {
        await this.submit_standard_answers(application_form_id);
        await this.submit_file_answers(application_form_id);
        this.submit_success_message = 'Your form was submitted successfully.';
        await this.load_selected_application();
      } catch (error) {
        this.portal_error = get_error_message(error, 'Unable to submit the form.');
      } finally {
        this.is_submitting = false;
      }
    },
    async handleApplicationChange() {
      await this.load_selected_application();
    },
    async handleFormChange() {
      await this.load_selected_form_structure();
    }
  }
};
